import { getRouteApi } from '@tanstack/react-router'

import { type SelectOption } from '@/components/Select/Select'
import ExportButton from '@/components/common/ExportButton'
import Filters from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { usePagination } from '@/hooks/usePagination'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import CoursesList from '../components/CoursesList'
import type { CoursesFiltersKey } from '../constants/courses-filters'
import useCoursesColumns from '../hooks/useCoursesColumns'
import useFilters from '../hooks/useFilters'
import useCoursesGroupsDownload from '../mutations/useCoursesGroupsDownload'
import useCoursesGroups from '../queries/useCoursesGroups'
import useCoursesGroupsCount from '../queries/useCoursesGroupsCount'

const routeApi = getRouteApi('/_auth/courses/all-courses')

const AllCoursesView = () => {
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const params = {
    ...search,
    ids: search.ids?.map(getValue),
    teacherOrCoTeacherId: search.teacherOrCoTeacherId?.map(getValue),
    studentsId: search.studentsId?.map(getValue),
    classesId: search.classesId?.map(getValue),
    coursesId: search.coursesId?.map(getValue)
  }

  const { data: groups, isLoading: isGroupsLoading } = useCoursesGroups(params)
  const { data: groupsCount, isLoading: isGroupsCountLoading } =
    useCoursesGroupsCount({
      semester: search.semester
    })

  const pagination = usePagination(routeApi)

  const handleChangeFilter = (
    key: CoursesFiltersKey,
    value?: string | string[] | SelectOption<string>[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        semester: search.semester,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const handleChangeSemester = (semesterId?: string) => {
    navigate({
      search: {
        semester: semesterId,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { filters, isSomeFilterSelected } = useFilters({
    ...search,
    onChangeFilter: handleChangeFilter,
    onChangeSemester: handleChangeSemester
  })

  const { columns, ...manageColumns } = useCoursesColumns({
    semesterId: search.semester,
    routeApi
  })

  const download = useCoursesGroupsDownload({
    ...params,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        manageColumns={manageColumns}
        actionsRender={<ExportButton {...download} />}
      />
      <CoursesList
        columns={columns}
        semesterId={search.semester}
        list={groups?.list || []}
        count={groups?.count || 0}
        totalCount={groupsCount || 0}
        loading={isGroupsLoading || isGroupsCountLoading}
        pagination={pagination}
      />
    </>
  )
}
export default AllCoursesView
